import { supabase } from "./supabase";
import { TESTFLIGHT_LINK } from "./appLink";

/* INVITING A CLIENT, from the web.
 *
 * 🔴 The parity walk of 2026-09-13 found the web only ever SELECTs `coach_clients` — there was no
 * way to add a client at all. A coach console you cannot add a client to is a reading surface.
 *
 * ⚠️ **NO POLICY CHANGE, AND THE COLUMN LIST IS WHY.** `coach_clients` grants the coach a
 * COLUMN-LEVEL insert — `coach_id`, `invited_email`, `invited_first_name`, `invited_last_name`,
 * `status` — and nothing else. `accepted_at` and `client_id` belong to the CLIENT's accept RPC
 * alone; that split is what stops a coach writing themselves into somebody's roster, and it is the
 * hole the 2026-09-06 security session closed. **So this insert names exactly those columns and
 * never reaches for the other two**, which is also why a coach's invitation is a doorbell rather
 * than a key.
 *
 * ⚠️ **A PLAIN INSERT, NOT AN UPSERT**, and the reason is written on the phone's copy: the unique
 * index is on `(coach_id, lower(invited_email))` WITH a status filter, and Postgres cannot match an
 * expression-based partial index to an `onConflict` clause — every invitation failed when that was
 * tried. The index still does its job; a duplicate raises and the caller gets a sentence.
 */

export type InviteOutcome =
  | { kind: "invited"; email: string; firstName: string }
  | { kind: "failed"; message: string };

export function inviteFault(email: string): string | null {
  const e = email.trim();
  if (e === "") return "Enter the email they will sign in with.";
  if (!e.includes("@")) return "That does not look like an email address.";
  return null;
}

export async function inviteClient(args: {
  coachId: string;
  email: string;
  firstName: string;
  lastName: string;
}): Promise<InviteOutcome> {
  const fault = inviteFault(args.email);
  if (fault !== null) return { kind: "failed", message: fault };

  // LOWERCASED, and that is not tidiness. `coach_clients` matches an invitation against
  // `auth.jwt() ->> 'email'`, the index is on `lower(invited_email)`, and an address stored with a
  // capital letter is an invitation its owner can never accept — the codex's own case-mismatch
  // family, one table over from the storage-path one that cost a month of broken photo uploads.
  const email = args.email.trim().toLowerCase();
  const first = args.firstName.trim();
  const last = args.lastName.trim();

  const { error } = await supabase.from("coach_clients").insert({
    coach_id: args.coachId,
    invited_email: email,
    invited_first_name: first === "" ? null : first,
    invited_last_name: last === "" ? null : last,
  });

  if (error) {
    return { kind: "failed", message: inviteErrorMessage(error.message, email) };
  }
  return { kind: "invited", email, firstName: first };
}

/* The duplicate is the common failure and its raw text is a constraint name. Everything else keeps
 * the server's own words — a rewritten error is a guess about a cause nobody measured. */
/**
 * 🔴 A LINK, NOT A FORM (2026-09-22 night, his direction): the coach types NOTHING. The row is born
 * with no address and no name — `20260922220000` made `invited_email` optional and the INSERT policy
 * still pins coach_id = auth.uid(), pending, no client, no consent stamp. The person who opens the
 * link brings their own details; the token (single-use, 30 days) is what attaches them.
 */
export type LinkOutcome = { kind: "link"; url: string } | { kind: "failed"; message: string };

export const JOIN_BASE = "https://coachwork.io/join/";

export async function createInviteLink(coachId: string): Promise<LinkOutcome> {
  const { data, error } = await supabase
    .from("coach_clients")
    .insert({ coach_id: coachId })
    .select("invite_token")
    .single();
  if (error) return { kind: "failed", message: error.message };
  const token = data?.invite_token ?? "";
  if (token === "") return { kind: "failed", message: "The invitation was made but carried no link. Try again." };
  return { kind: "link", url: JOIN_BASE + token };
}

export function inviteErrorMessage(raw: string, email: string): string {
  if (raw.includes("duplicate key") || raw.includes("coach_clients_live_email_idx")) {
    return `${email} is already on your roster, or has an invitation waiting.`;
  }
  return raw;
}

/* WHAT THE COACH SENDS THEM — F13 STEP 1.
 *
 * ⚠️ **THE SAME WORDS AS THE PHONE**, because a client may be invited from either and two different
 * invitations describing the same app is how a coach starts explaining their own tooling. The
 * phone's copy is `CoachInvitationMessage.swift`; a gate compares the two texts, because two
 * surfaces that agree today are one edit from disagreeing.
 *
 * 🔴 **IT CARRIES THE LINK NOW, AND THAT REVERSES A DELIBERATE REFUSAL.** The old message named
 * the App Store and no URL, on the ground that universal links do not exist so any link would 404.
 * That reasoning was about `coachwork.io/join/<token>` — F13's step 3 — and it was right about it.
 * It was never true of the PUBLIC TESTFLIGHT LINK, which resolves for anybody and installs the
 * app: MEASURED live (`appLink.ts`). Meanwhile "download it from the App Store" named a listing
 * **that does not exist** — the App Store has no Coachwork — so the honest-sounding sentence was
 * the one sending people nowhere, and that is what his coach friend's girlfriend was handed.
 *
 * THREE STEPS, NUMBERED, because the install is genuinely three actions and a client who stalls
 * at TestFlight is a client the coach has to talk through it. The ADDRESS keeps its own step: it
 * is the matching mechanism and losing it costs the whole invitation.
 */
export function invitationMessage(
  firstName: string,
  email: string,
  link: string = TESTFLIGHT_LINK,
  joinLink?: string,
): string {
  const hello = firstName === "" ? "Hi" : `Hi ${firstName}`;
  // 🔴 WHEN THE INVITATION HAS A LINK OF ITS OWN, THE ADDRESS STOPS BEING A STEP — and the phone
  // says the same thing, word for word, because a gate compares the two. Step 3 used to insist on
  // the exact address, which was true and was also the failure: Sign in with Apple's "Hide My
  // Email" gives a different one deliberately. `invite_token` replaced that match.
  // 🔴 AND SINCE 2026-09-22 NIGHT THE LINK IS THE WHOLE INVITATION (his words: *"Just download the
  // app with a link and then add all their details, then when done, they will be instantly linked
  // with that coach"*). The link page is one button that gets the app and carries the invitation
  // into it, so the message stops being a procedure: no web account, no "accept" step, no second
  // link to install from. One sentence, one link.
  if (joinLink) {
    return `${hello} — I'll be writing your training in Coachwork.

Tap this on your iPhone to join: ${joinLink}

It gets you the app and connects you to me. Your program will be waiting.`;
  }
  return `${hello} — I'll be writing your training in Coachwork.

1. Open this on your iPhone: ${link}
2. Install TestFlight if you don't have it, then install Coachwork.
3. Sign up with ${email} — it has to be that address, that's how it finds my invitation.

Your program will be waiting.`;
}
